export interface ConversationFooterInputState {
	show: boolean;
	disabled: boolean;
	placeholder: string;
}

export type ConversationLifecycleMachineState =
	| { phase: "idle" }
	| { phase: "creating" }
	| { phase: "create_failed"; error: string }
	| { phase: "polling"; conversationId: string }
	| {
			phase: "submitting";
			conversationId: string;
			optimisticUserMessage: string;
			terminalStatusPolls: number;
	  }
	| { phase: "cancelled"; conversationId: string }
	| { phase: "complete"; conversationId: string }
	| { phase: "error"; conversationId: string; error: string }
	| { phase: "send_failed"; conversationId: string; error: string };

export const getConversationId = (state: ConversationLifecycleMachineState): string | undefined =>
	"conversationId" in state ? state.conversationId : undefined;

export const INITIAL_CONVERSATION_MACHINE_STATE: ConversationLifecycleMachineState = { phase: "idle" };

export type ConversationLifecycleMachineEvent =
	| { type: "RESET" }
	| { type: "LOAD_EXISTING"; conversationId: string }
	| { type: "START_REQUEST" }
	| { type: "START_SUCCESS"; conversationId: string }
	| { type: "START_FAILURE"; error: string }
	| { type: "SEND_REQUEST"; message: string }
	| { type: "SEND_SUCCESS" }
	| { type: "CANCEL_SUCCESS" }
	| { type: "CANCEL_FAILURE"; error: string }
	| { type: "SEND_FAILURE"; error: string }
	| { type: "STATUS_IN_PROGRESS" }
	| { type: "STATUS_COMPLETE" }
	| { type: "STATUS_FAILURE"; error: string }
	| { type: "STALE_SESSION_DETECTED" };

const TERMINAL_POLL_THRESHOLD = 2;

const handleStatusComplete = (state: ConversationLifecycleMachineState): ConversationLifecycleMachineState => {
	if (state.phase === "submitting") {
		const terminalStatusPolls = state.terminalStatusPolls + 1;
		if (terminalStatusPolls < TERMINAL_POLL_THRESHOLD) {
			return { ...state, terminalStatusPolls };
		}
		return { phase: "complete", conversationId: state.conversationId };
	}
	if (state.phase === "cancelled") {
		return { phase: "complete", conversationId: state.conversationId };
	}
	if (state.phase === "complete") {
		return state;
	}
	const conversationId = getConversationId(state);
	if (conversationId == null) {
		return state;
	}
	return { phase: "complete", conversationId };
};

const handleStatusFailure = (
	state: ConversationLifecycleMachineState,
	error: string,
): ConversationLifecycleMachineState => {
	if (state.phase === "submitting") {
		const terminalStatusPolls = state.terminalStatusPolls + 1;
		if (terminalStatusPolls < TERMINAL_POLL_THRESHOLD) {
			return { ...state, terminalStatusPolls };
		}
		return { phase: "error", conversationId: state.conversationId, error };
	}
	if (state.phase === "error" && state.error === error) {
		return state;
	}
	const conversationId = getConversationId(state);
	if (conversationId == null) {
		return state;
	}
	return { phase: "error", conversationId, error };
};

export const conversationLifecycleReducer = (
	state: ConversationLifecycleMachineState,
	event: ConversationLifecycleMachineEvent,
): ConversationLifecycleMachineState => {
	switch (event.type) {
		case "RESET":
		case "STALE_SESSION_DETECTED":
			return INITIAL_CONVERSATION_MACHINE_STATE;
		case "LOAD_EXISTING":
			return { phase: "polling", conversationId: event.conversationId };
		case "START_REQUEST":
			return { phase: "creating" };
		case "START_SUCCESS":
			return { phase: "polling", conversationId: event.conversationId };
		case "START_FAILURE":
			return { phase: "create_failed", error: event.error };
		case "SEND_REQUEST": {
			const conversationId = getConversationId(state);
			if (conversationId == null) {
				return state;
			}
			return { phase: "submitting", conversationId, optimisticUserMessage: event.message, terminalStatusPolls: 0 };
		}
		case "SEND_SUCCESS":
			return state;
		case "CANCEL_SUCCESS": {
			const conversationId = getConversationId(state);
			if (conversationId == null) {
				return state;
			}
			return { phase: "cancelled", conversationId };
		}
		case "CANCEL_FAILURE": {
			const conversationId = getConversationId(state);
			if (conversationId == null) {
				return state;
			}
			return { phase: "error", conversationId, error: event.error };
		}
		case "SEND_FAILURE": {
			const conversationId = getConversationId(state);
			if (conversationId == null) {
				return state;
			}
			return { phase: "send_failed", conversationId, error: event.error };
		}
		case "STATUS_IN_PROGRESS": {
			if (state.phase === "polling") {
				return state;
			}
			const conversationId = getConversationId(state);
			if (conversationId == null) {
				return state;
			}
			return { phase: "polling", conversationId };
		}
		case "STATUS_COMPLETE":
			return handleStatusComplete(state);
		case "STATUS_FAILURE":
			return handleStatusFailure(state, event.error);
	}
};

const IDLE_PLACEHOLDER = "Ask Liz anything...";
const COMPLETE_PLACEHOLDER = "Message Liz...";

export const selectConversationFooterInput = (
	state: ConversationLifecycleMachineState,
	isSubmittingMessage: boolean,
): ConversationFooterInputState => {
	const isProcessing = state.phase === "creating" || state.phase === "polling" || state.phase === "submitting";
	return {
		show: state.phase !== "create_failed",
		disabled: isProcessing || isSubmittingMessage,
		placeholder:
			state.phase === "idle" ? IDLE_PLACEHOLDER : state.phase === "cancelled" ? "Message Liz..." : COMPLETE_PLACEHOLDER,
	};
};
